// src/components/admin/blockEditors/TextBlockEditor.tsx - VERSÃO CORRIGIDA

import React, { useState, useEffect, useRef } from 'react';
import { TextBlock } from '../../../types/contentBlocks.types';
import {
    Bold, Italic, Underline, AlignLeft, AlignCenter, List,
    CheckCircle, AlertCircle, Info, Lightbulb, TrendingUp, // ✅ ADICIONAR
    Award, Target, Zap, BookOpen, Brain // ✅ ADICIONAR
} from 'lucide-react';
import './BlockEditors.css';

interface TextBlockEditorProps {
    block: TextBlock;
    onChange: (block: TextBlock) => void;
}

export const TextBlockEditor: React.FC<TextBlockEditorProps> = ({ block, onChange }) => {
    const [title, setTitle] = useState(block.data.title);
    const [alignment, setAlignment] = useState(block.data.alignment || 'left');
    const contentRef = useRef<HTMLDivElement>(null);
    const [showIconMenu, setShowIconMenu] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const content = contentRef.current?.innerHTML || '';
            onChange({
                ...block,
                data: { title, content, alignment }
            });
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, alignment]);

    useEffect(() => {
        if (contentRef.current && contentRef.current.innerHTML !== block.data.content) {
            contentRef.current.innerHTML = block.data.content;
        }
    }, [block.data.content]);

    const execCommand = (command: string, value: string | null = null) => {
        document.execCommand(command, false, value ?? undefined);
        contentRef.current?.focus();
    };

    const insertIcon = (iconName: string) => {
        const iconMap: { [key: string]: any } = {
            'check-circle': CheckCircle,
            'alert-circle': AlertCircle,
            'info': Info,
            'lightbulb': Lightbulb,
            'trending-up': TrendingUp,
            'award': Award,
            'target': Target,
            'zap': Zap,
            'book-open': BookOpen,
            'brain': Brain
        };

        const IconComponent = iconMap[iconName];
        if (!IconComponent) return;

        const iconHtml = `<span class="inline-icon" style="color: #003BD1; display: inline-flex; align-items: center; margin: 0 4px;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${getIconPath(iconName)}</svg></span>`;

        document.execCommand('insertHTML', false, iconHtml);
        contentRef.current?.focus();
    };

    const getIconPath = (iconName: string): string => {
        const paths: { [key: string]: string } = {
            'check-circle': '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>',
            'alert-circle': '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>',
            'info': '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>',
            'lightbulb': '<line x1="9" y1="18" x2="15" y2="18"></line><line x1="10" y1="22" x2="14" y2="22"></line><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>',
            'trending-up': '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline>',
            'award': '<circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>',
            'target': '<circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle>',
            'zap': '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>',
            'book-open': '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>',
            'brain': '<path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"></path><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"></path>'
        };
        return paths[iconName] || '';
    };

    const handleInput = () => {
        if (contentRef.current) {
            const content = contentRef.current.innerHTML;
            onChange({
                ...block,
                data: { title, content, alignment }
            });
        }
    };

    return (
        <div className="be-container">
            <div className="be-form-group">
                <label className="be-label">Título</label>
                <input
                    type="text"
                    className="be-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: O que você vai aprender"
                />
            </div>

            {/* Toolbar */}
            <div className="be-editor-toolbar">
                <button
                    type="button"
                    onClick={() => execCommand('bold')}
                    className="be-toolbar-btn"
                    title="Negrito"
                >
                    <Bold size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('italic')}
                    className="be-toolbar-btn"
                    title="Itálico"
                >
                    <Italic size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => execCommand('underline')}
                    className="be-toolbar-btn"
                    title="Sublinhado"
                >
                    <Underline size={16} />
                </button>

                <div className="be-toolbar-divider" />

                <button
                    type="button"
                    onClick={() => setAlignment('left')}
                    className={`be-toolbar-btn ${alignment === 'left' ? 'active' : ''}`}
                    title="Alinhar à esquerda"
                >
                    <AlignLeft size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => setAlignment('center')}
                    className={`be-toolbar-btn ${alignment === 'center' ? 'active' : ''}`}
                    title="Centralizar"
                >
                    <AlignCenter size={16} />
                </button>

                <div className="be-toolbar-divider" />

                <button
                    type="button"
                    onClick={() => execCommand('insertUnorderedList')}
                    className="be-toolbar-btn"
                    title="Lista com marcadores"
                >
                    <List size={16} />
                </button>

                <div className="be-icon-dropdown-container">
                    <button
                        type="button"
                        onClick={() => setShowIconMenu(!showIconMenu)}
                        className="be-toolbar-btn"
                        title="Inserir ícone"
                    >
                        <Zap size={16} />
                    </button>
                    {showIconMenu && (
                        <div className="be-icon-dropdown">
                            {[
                                { name: 'check-circle', Icon: CheckCircle, label: 'Check' },
                                { name: 'alert-circle', Icon: AlertCircle, label: 'Alerta' },
                                { name: 'info', Icon: Info, label: 'Info' },
                                { name: 'lightbulb', Icon: Lightbulb, label: 'Ideia' },
                                { name: 'trending-up', Icon: TrendingUp, label: 'Crescimento' },
                                { name: 'award', Icon: Award, label: 'Conquista' },
                                { name: 'target', Icon: Target, label: 'Meta' },
                                { name: 'zap', Icon: Zap, label: 'Ação' },
                                { name: 'book-open', Icon: BookOpen, label: 'Estudo' },
                                { name: 'brain', Icon: Brain, label: 'Conhecimento' }
                            ].map(({ name, Icon, label }) => (
                                <button
                                    key={name}
                                    type="button"
                                    className="be-icon-dropdown-option"
                                    onClick={() => {
                                        insertIcon(name);
                                        setShowIconMenu(false);
                                    }}
                                >
                                    <Icon size={18} style={{ color: '#003BD1' }} />
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Editor de conteúdo */}
            <div className="be-form-group">
                <label className="be-label">Conteúdo</label>
                <div
                    ref={contentRef}
                    contentEditable
                    onInput={handleInput}
                    className="be-content-editor"
                    data-placeholder="Escreva o conteúdo aqui..."
                    style={{ textAlign: alignment }}
                />
            </div>
        </div>
    );
};
